import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Card, Box, Typography, Button, useMediaQuery } from '@mui/material';
import RichTextBlock from './RichTextBlock';
import { styled, useTheme } from '@mui/material/styles';
import { Link } from 'react-router-dom';
import DownloadIcon from '@mui/icons-material/Download';



// Tarjeta principal
const StyledCard = styled(Card, {
  shouldForwardProp: (prop) => prop !== 'expanded'
})(({ theme, expanded }) => ({
  position: 'relative',
  overflow: 'hidden',
  backgroundColor: expanded
    ? theme.palette.primary.main
    : '#fff',
  color: expanded
    ? '#fff'
    : theme.palette.primary.main,
  transition: 'color 0.4s, background 0.4s, box-shadow 0.4s, height 0.4s',
  cursor: 'pointer',
  textDecoration: 'none',
  minWidth: '255px',
  width: '255px',
  height: '200px',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-start',
  borderRadius: '10px',
  boxShadow: '1px 2px 22px -3px rgba(0,0,0,0.61)',
  boxSizing: 'border-box',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    right: 0,
    width: '0%',
    height: '100%',
    backgroundColor: theme.palette.primary.main,
    zIndex: 0,
    transition: 'width 0.4s ease',
  },
  [theme.breakpoints.up('md')]: {
    '&:hover::before': {
      width: '100%',
    },
    '&:hover': {
      color: '#fff',
    },
    '&:hover .extra-info': {
      opacity: 1,
      maxHeight: '100px',
    },
  },
  [theme.breakpoints.down('md')]: {
    width: '100%',
    minWidth: '100%',
    maxWidth: '100%',
    height: expanded ? 'auto' : '50px',
    borderRadius: '8px',
    padding: expanded ? theme.spacing(2) : theme.spacing(0.5, 1),
    boxShadow: expanded
      ? '0 4px 16px rgba(0,0,0,0.18)'
      : '0 1px 4px rgba(0,0,0,0.10)',
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    alignItems: 'stretch',
    justifyContent: 'flex-start',
    zIndex: expanded ? 10 : 1,
  },
}));

const TitleRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  marginBottom: 8,
  flexShrink: 0,
  width: '100%',
  [theme.breakpoints.down('md')]: {
    marginBottom: 0,
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing(1),
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
  },
}));

const Description = styled(Typography)(({ theme }) => ({
  flexShrink: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  minHeight: '40px',
  maxHeight: '40px',
  [theme.breakpoints.down('md')]: {
    color: 'inherit',
    minHeight: 0,
    maxHeight: 'none',
    marginBottom: 0,
    display: 'block',
    WebkitLineClamp: 'unset',
    WebkitBoxOrient: 'unset',
    overflow: 'visible',
    textOverflow: 'unset',
  },
}));

const ExtraInfo = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'expanded',
})(({ theme, expanded }) => ({
  opacity: expanded ? 1 : 0,
  maxHeight: expanded ? 300 : 0,
  overflow: expanded ? 'visible' : 'hidden',
  transition: 'opacity 0.4s, max-height 0.4s',
  marginTop: 0,
  width: '100%',
  color: 'inherit',
  [theme.breakpoints.down('md')]: {
    opacity: expanded ? 1 : 0,
    maxHeight: expanded ? 300 : 0,
    overflow: expanded ? 'visible' : 'hidden',
    marginTop: 0,
    color: 'inherit',
  },
}));

const QuickAccessCard = ({ icon, title, description, extraInfo, route, pdf }) => {
  const [expanded, setExpanded] = useState(false);
  const theme = useTheme();
  const isTouch = useMediaQuery(theme.breakpoints.down('md'));

  // Detect route type early so we can show a download icon when route ends with .pdf
  const isRouteString = typeof route === 'string' && route.length > 0;
  const isExternal = isRouteString && /^https?:\/\//i.test(route);
  const isRoutePdf = isRouteString && route.toLowerCase().endsWith('.pdf');



  // Desktop content (mantiene el diseño actual)
  const desktopContent = (
    <Box
      position="relative"
      zIndex={1}
      padding={2}
      boxSizing="border-box"
      display="flex"
      flexDirection="column"
      flex={1}
      sx={{ display: { xs: 'none', sm: 'flex' } }}
    >
      <TitleRow>
        {icon && (
          <Box sx={{ mr: 1 }}>
            {typeof icon === 'string' ? (
              <Box
                component="img"
                src={icon}
                alt={title}
                sx={{
                  width: 24,
                  height: 24,
                  objectFit: 'contain',
                }}
              />
            ) : (
              icon
            )}
          </Box>
        )}
        <Typography
          variant="h6"
          component="h2"
          sx={theme => ({
            flex: 1,
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            [theme.breakpoints.down('md')]: {
              whiteSpace: 'normal',
              width: '100%',
            },
          })}
        >
          {title}
        </Typography>
      </TitleRow>
      <Description variant="body2">
        {typeof description === 'object' && description !== null
          ? <RichTextBlock content={description} />
          : description}
      </Description>
      {extraInfo && (
        <ExtraInfo className="extra-info" expanded={false}>
          <Typography variant="body2" sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical',
            maxHeight: '60px',
          }}>
            {extraInfo}
          </Typography>
        </ExtraInfo>
      )}
      {(pdf || isRoutePdf) && (
  <Box
    sx={{
      position: 'absolute',
      bottom: 16,
      right: 16,
      zIndex: 2,
      display: { xs: 'none', md: 'flex' },
      alignItems: 'center',
      justifyContent: 'center',
      bgcolor: 'rgba(255,255,255,0.85)',
      borderRadius: '50%',
      width: 32,
      height: 32,
      boxShadow: 2,
      opacity: 0,
      transition: 'opacity 0.3s',
      pointerEvents: 'none', // Solo visual, no click
      '.quick-access-card:hover &': {
        opacity: 1,
      },
    }}
  >
    <DownloadIcon sx={{ color: theme => theme.palette.primary.main, fontSize: 28 }} />
  </Box>
)}
    </Box>
  );

  // Mobile content (icono, título, y contenido expandido inline)
  const mobileContent = (
    <>
      <TitleRow>
        {icon && (
          <Box sx={{ mr: 1, flexShrink: 0 }}>
            {typeof icon === 'string' ? (
              <Box
                component="img"
                src={icon}
                alt={title}
                sx={{
                  width: 24,
                  height: 24,
                  objectFit: 'contain',
                }}
              />
            ) : (
              icon
            )}
          </Box>
        )}
        <Typography
          variant="subtitle1"
          component="h2"
          sx={{
            flex: 1,
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            fontWeight: 600,
            color: 'inherit',
          }}
        >
          {title}
        </Typography>
      </TitleRow>
      {expanded && (
        <>
          {description && (
            <Description variant="body2" sx={{ color: 'inherit' }}>
              {description}
            </Description>
          )}
          {extraInfo && (
            <ExtraInfo expanded={expanded}>
              <Typography variant="body2" sx={{ color: 'inherit' }}>
                {extraInfo}
              </Typography>
            </ExtraInfo>
          )}
          {pdf && (
            <Button
              variant="contained"
              color="secondary"
              href={pdf}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                mt:1,
                fontWeight: 600,
                borderRadius: 2,
                boxShadow: 'none',
                width: '100%',
              }}
              onClick={e => e.stopPropagation()}
            >
              Ver PDF
            </Button>
          )}
          {route && !pdf && (
            <Button
              variant="contained"
              color="secondary"
              onClick={e => {
                e.stopPropagation(); // <-- ¡Evita que el click cierre la tarjeta!
                const isExternalRoute = /^https?:\/\//i.test(route);
                const isRoutePdf = typeof route === 'string' && route.toLowerCase().endsWith('.pdf');
                if (isExternalRoute || isRoutePdf) {
                  window.open(route, '_blank', 'noopener');
                } else {
                  window.location.href = route;
                }
              }}
              sx={{
                mt:1,
                fontWeight: 600,
                borderRadius: 2,
                boxShadow: 'none',
                width: '100%',
              }}
            >
              Ver Más
            </Button>
          )}
        </>
      )}
    </>
  );

  // --- Renderizado final ---
  if (isTouch) {
    return (
      <StyledCard
        className="quick-access-card"
        expanded={expanded ? 1 : 0}
        onClick={() => setExpanded(exp => !exp)}
        sx={{ cursor: 'pointer' }}
      >
        <Box sx={{
          position: 'relative', 
          zIndex: 1,
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center', // centra verticalmente
          alignItems: 'flex-start', // alinea a la izquierda
          height: '100%',
        }}>
          {mobileContent}
        </Box>
      </StyledCard>
    );
  }

  // --- Desktop: comportamiento clásico ---
    if (!isTouch && (pdf || isRoutePdf || isExternal)) {
      // Si hay pdf (campo separado) o la ruta apunta a un pdf, o la ruta es externa,
      // abrimos en nueva pestaña con <a>
      const href = pdf || route;
      return (
        <a href={href} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
          <StyledCard className="quick-access-card">{desktopContent}</StyledCard>
        </a>
      );
    }

    if (!isTouch && route) {
      // Ruta local interna: usar Link
      return <Link to={route} style={{ textDecoration: 'none' }}>{<StyledCard className="quick-access-card">{desktopContent}</StyledCard>}</Link>;
    }

  // Si no hay ruta ni pdf, renderiza solo la tarjeta
  return (
    <StyledCard className="quick-access-card">
      {isTouch ? mobileContent : desktopContent}
    </StyledCard>
  );
};

QuickAccessCard.propTypes = {
  icon: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  extraInfo: PropTypes.string,
  route: PropTypes.string,
  pdf: PropTypes.string,
};

export default QuickAccessCard;
